import { api } from '../../lib/api-client';
import { sumQty } from './partsDraft';

export type JobStatus =
  | 'WAITING_RECEPTION'
  | 'WAITING_TECHNICIAN'
  | 'DIAGNOSIS'
  | 'WAITING_CUSTOMER_APPROVAL'
  | 'WAITING_PARTS'
  | 'WORK_IN_PROGRESS'
  | 'QUALITY_CONTROL'
  | 'READY_FOR_BILLING'
  | 'READY_FOR_COLLECTION'
  | 'COMPLETED'
  | 'CLOSED'
  | 'ON_HOLD'
  | 'WARRANTY_CLAIM'
  | 'INSURANCE_CLAIM';

export type JobType = 'SERVICE' | 'REPAIR' | 'WARRANTY' | 'INSURANCE' | 'ACCESSORY' | 'PDI';
export type JobActivity = 'DIAGNOSIS' | 'REPAIR' | 'QC' | 'WAIT';
export type JobLineType = 'LABOUR' | 'PART' | 'CONSUMABLE' | 'ACCESSORY' | 'OUTSOURCE';
export type JobLineStatus = 'ESTIMATED' | 'APPROVED' | 'ISSUED' | 'COMPLETED' | 'CANCELLED' | 'RETURNED';
export type QcResult = 'PASS' | 'FAIL' | 'NA';
export type ApprovalChannel = 'IN_PERSON' | 'WHATSAPP' | 'CALL' | 'SMS';
export type ApprovalKind = 'ESTIMATE' | 'ADDITIONAL_WORK';

export interface JobLine {
  uid: string;
  type: JobLineType;
  status: JobLineStatus;
  description: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  isBillable: boolean;
  technicianName: string | null;
  productiveSeconds: number | null;
  adjustedHours: string | null;
  adjustmentReason: string | null;
  approvedByCustomerAt: string | null;
}

export interface JobComplaint {
  uid: string;
  seq: number;
  complaint: string;
  diagnosis: string | null;
  cause: string | null;
  correction: string | null;
  technicianName: string | null;
}

export interface JobQcItem {
  uid: string;
  code: string;
  label: string;
  result: QcResult | null;
  note: string | null;
}

/** Money is a decimal STRING on this boundary — see lib/money.ts for why. */
export interface JobCard {
  uid: string;
  jobNo: string;
  status: JobStatus;
  statusLabel: string;
  jobType: JobType;
  customerUid: string;
  customerName: string;
  customerPhone: string | null;
  unitUid: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  mileageIn: number | null;
  mileageOut: number | null;
  serviceAdvisorName: string | null;
  arrivedAt: string;
  promisedAt: string | null;
  isLate: boolean;
  completedAt: string | null;
  closedAt: string | null;
  isComeback: boolean;
  comebackOfJobNo: string | null;
  billableTotal: string;
  approvedAmount: string | null;
  complaints: JobComplaint[];
  lines: JobLine[];
  qcItems: JobQcItem[];
  technicians: { uid: string; name: string; running: boolean }[];
  billingBlockers: string[];
  branchCode: string | null;
}

export interface JobCardPage {
  items: JobCard[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface SearchHitUnit {
  uid: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  colour: string | null;
  currentMileage: number | null;
  lastJobNo: string | null;
  lastJobAt: string | null;
}

export interface SearchHit {
  customerUid: string;
  customerNo: string;
  customerName: string;
  phone: string | null;
  matchedOn: 'phone' | 'plate' | 'chassis' | 'customerNo' | 'jobNo' | 'name';
  units: SearchHitUnit[];
  openJobs: { uid: string; jobNo: string; status: JobStatus }[];
}

export interface Timer {
  uid: string;
  jobCardUid: string;
  jobNo: string;
  activity: JobActivity;
  startedAt: string;
  elapsedSeconds: number;
}

export interface Approval {
  uid: string;
  kind: ApprovalKind;
  amountBefore: string;
  amountAfter: string;
  channel: string;
  approvedByName: string;
  approvedAt: string;
  recordedByName: string | null;
}

export function searchCounter(q: string): Promise<SearchHit[]> {
  return api<SearchHit[]>(`/workshop/search?q=${encodeURIComponent(q)}`);
}

export function listJobCards(params: {
  q?: string;
  status?: JobStatus;
  open?: boolean;
  late?: boolean;
  isComeback?: boolean;
  unitUid?: string;
  customerUid?: string;
  cursor?: string;
}): Promise<JobCardPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.open === true) search.set('open', 'true');
  if (params.late === true) search.set('late', 'true');
  if (params.isComeback === true) search.set('isComeback', 'true');
  if (params.unitUid !== undefined) search.set('unitUid', params.unitUid);
  if (params.customerUid !== undefined) search.set('customerUid', params.customerUid);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<JobCardPage>(`/workshop/job-cards${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getJobCard(uid: string): Promise<JobCard> {
  return api<JobCard>(`/workshop/job-cards/${uid}`);
}

export function createJobCard(input: {
  customerUid: string;
  unitUid: string;
  jobType: JobType;
  mileageIn?: number;
  complaints?: string[];
  promisedAt?: string;
}): Promise<JobCard> {
  return api<JobCard>('/workshop/job-cards', { method: 'POST', body: input });
}

export function changeJobStatus(
  uid: string,
  input: { status: JobStatus; mileageOut?: number; reason?: string },
): Promise<JobCard> {
  return api<JobCard>(`/workshop/job-cards/${uid}/transitions/status`, {
    method: 'POST',
    body: input,
  });
}

export function addComplaint(uid: string, input: { complaint: string }): Promise<JobCard> {
  return api<JobCard>(`/workshop/job-cards/${uid}/complaints`, { method: 'POST', body: input });
}

export function updateComplaint(
  complaintUid: string,
  input: { diagnosis?: string; cause?: string; correction?: string },
): Promise<JobCard> {
  return api<JobCard>(`/workshop/complaints/${complaintUid}`, { method: 'PATCH', body: input });
}

export function assignTechnician(uid: string, input: { technicianUid: string }): Promise<JobCard> {
  return api<JobCard>(`/workshop/job-cards/${uid}/technicians`, { method: 'POST', body: input });
}

export function releaseTechnician(uid: string, technicianUid: string): Promise<JobCard> {
  return api<JobCard>(`/workshop/job-cards/${uid}/technicians/${technicianUid}/release`, {
    method: 'POST',
    body: {},
  });
}

export function startTimer(uid: string, input: { activity: JobActivity }): Promise<Timer> {
  return api<Timer>(`/workshop/job-cards/${uid}/timer/start`, { method: 'POST', body: input });
}

export function stopTimer(): Promise<{ stoppedSeconds: number; description: string }> {
  return api<{ stoppedSeconds: number; description: string }>('/workshop/timer/stop', {
    method: 'POST',
    body: {},
  });
}

export function myTimer(): Promise<Timer | null> {
  return api<Timer | null>('/workshop/my-timer');
}

export function myJobs(): Promise<JobCard[]> {
  return api<JobCard[]>('/workshop/my-jobs');
}

export function adjustLabour(
  lineUid: string,
  input: { hours: string; reason: string },
): Promise<{ hours: string; total: string }> {
  return api<{ hours: string; total: string }>(`/workshop/lines/${lineUid}/hours`, {
    method: 'PATCH',
    body: input,
  });
}

export function recordApproval(
  uid: string,
  input: {
    kind: ApprovalKind;
    amountAfter: string;
    channel: ApprovalChannel;
    approvedByName: string;
  },
): Promise<Approval> {
  return api<Approval>(`/workshop/job-cards/${uid}/approvals`, { method: 'POST', body: input });
}

export function listApprovals(uid: string): Promise<Approval[]> {
  return api<Approval[]>(`/workshop/job-cards/${uid}/approvals`);
}

export function recordQc(itemUid: string, input: { result: QcResult; note?: string }): Promise<JobCard> {
  return api<JobCard>(`/workshop/qc-items/${itemUid}`, { method: 'PATCH', body: input });
}

export interface TechnicianOption {
  uid: string;
  name: string;
  gradeCode: string | null;
  assigned: boolean;
}

export function listTechnicians(jobCardUid: string): Promise<TechnicianOption[]> {
  return api<TechnicianOption[]>(`/workshop/technicians?jobCardUid=${encodeURIComponent(jobCardUid)}`);
}

// Parts, as the job card sees them. Declared here rather than imported from
// features/parts: features do not import each other (CLAUDE.md §4).
export type JobPartsStatus =
  'REQUESTED' | 'RESERVED' | 'ISSUED' | 'PARTIALLY_ISSUED' | 'BACKORDER' | 'CANCELLED';

export interface JobPartsRequest {
  uid: string;
  requestNo: string;
  status: JobPartsStatus;
  requestedAt: string;
  note: string | null;
  lines: {
    uid: string;
    skuCode: string;
    skuName: string;
    qtyRequested: string;
    qtyIssued: string;
  }[];
}

export interface PartChoice {
  uid: string;
  skuCode: string;
  name: string;
  uom: string;
  /** What the DMS can put aside right now: on hand less what is reserved. */
  totalAvailable: string;
  /** AutoCount's last-known figure, summed over locations; null if never read. */
  autocountQty: string | null;
}

interface SkuRow {
  uid: string;
  skuCode: string;
  name: string;
  uom: string;
  totalAvailable: string;
  balances: { autocountQty: string | null }[];
}

export async function searchParts(q: string): Promise<PartChoice[]> {
  const page = await api<{ items: SkuRow[] }>(`/parts/skus?q=${encodeURIComponent(q)}`);
  return page.items.map((s) => {
    const read = s.balances.filter((b) => b.autocountQty !== null);
    const autocountQty =
      read.length === 0 ? null : sumQty(read.map((b) => b.autocountQty ?? '0'));
    return {
      uid: s.uid,
      skuCode: s.skuCode,
      name: s.name,
      uom: s.uom,
      totalAvailable: s.totalAvailable,
      autocountQty,
    };
  });
}

export function listJobPartsRequests(jobCardUid: string): Promise<{ items: JobPartsRequest[] }> {
  return api<{ items: JobPartsRequest[] }>(
    `/parts/requests?jobCardUid=${encodeURIComponent(jobCardUid)}`,
  );
}

export function requestJobParts(input: {
  jobCardUid: string;
  /** Where to put the parts aside; omitted, the branch's main location. */
  locationUid?: string;
  lines: { skuUid: string; qty: string }[];
  note?: string;
}): Promise<JobPartsRequest> {
  return api<JobPartsRequest>('/parts/requests', { method: 'POST', body: input });
}

export function cancelJobPartsRequest(uid: string, input: { reason: string }): Promise<JobPartsRequest> {
  return api<JobPartsRequest>(`/parts/requests/${uid}/transitions/cancel`, {
    method: 'POST',
    body: input,
  });
}
